/* ============================================================
   Fence Works — shared UI primitives
   ============================================================ */
import React from "react";
import * as ReactDOM from "react-dom";
import { sampleJob } from "./lib/samplejob";

/* Overlay portal. Every modal / scrim / lightbox renders through this into
   <body> so no ancestor (transform, filter, animation, contain) can capture
   its position:fixed and drop it into the page's scroll height — the popup
   always centers on the viewport the user is looking at. */
function Portal({ children }: any) {
  if (typeof document === "undefined") return children;
  return ReactDOM.createPortal(children, document.body);
}

/* ---- FenceWorks survey-diamond mark ----
   Canonical static mark is the "mid set" frame of the draw-in animation
   (white post still being set at the bottom). variant="final" renders the
   finished mark (all posts set) — swapping the brand default is one prop. */
function LogoMark({ size = 30, variant = "mid" }: any) {
  const fin = variant === "final";
  return (
    <svg className="logo-mark" width={size} height={size} viewBox="0 0 64 64" fill="none" role="img" aria-label="FenceWorks">
      {!fin && <path d="M6 32 32 6 58 32 32 58Z" stroke="#2dd4bf" strokeWidth="2.6" opacity=".15" strokeLinecap="round" strokeLinejoin="round" />}
      <path d="M32 6V58M6 32H58" stroke="#2dd4bf" strokeWidth="1.6" opacity={fin ? ".3" : ".24"} strokeDasharray="2.5 4.5" strokeLinecap="round" />
      {fin
        ? <path d="M6 32 32 6 58 32 32 58Z" stroke="#22e3d4" strokeWidth="3" strokeLinejoin="round" />
        : <path d="M6 32 32 6 58 32 32 58" stroke="#22e3d4" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}
      <circle cx="32" cy="32" r="6" stroke={fin ? "#22e3d4" : "#2dd4bf"} strokeWidth="3" opacity={fin ? "1" : ".5"} />
      <circle cx="32" cy="32" r="1.9" fill="#22e3d4" />
      <circle cx="6" cy="32" r="3.4" fill="#22e3d4" />
      <circle cx="32" cy="6" r="3.4" fill="#22e3d4" />
      <circle cx="58" cy="32" r="3.4" fill="#22e3d4" />
      {fin ? <circle cx="32" cy="58" r="3.4" fill="#22e3d4" /> : <circle cx="32" cy="58" r="3.7" fill="#eafff9" />}
    </svg>
  );
}

/* play-once mark — draws in on mount, settles, replays on hover.
   For splash moments (auth screen, hero); honors prefers-reduced-motion. */
function LogoMarkOnce({ size = 36 }: any) {
  const replay = (e: any) => {
    for (const el of e.currentTarget.querySelectorAll(".po-draw,.po-ring,.po-dot,.po-post,.po-head")) {
      el.style.animation = "none"; void el.getBoundingClientRect(); el.style.animation = "";
    }
  };
  return (
    <svg className="logo-mark" width={size} height={size} viewBox="0 0 64 64" fill="none" role="img" aria-label="FenceWorks" onMouseEnter={replay}>
      <defs><style>{`
        .po-draw{fill:none;stroke:#22e3d4;stroke-width:3;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:148;stroke-dashoffset:148;animation:poDraw 2s cubic-bezier(.45,0,.2,1) forwards}
        .po-ring{fill:none;stroke:#22e3d4;stroke-width:3;opacity:0;animation:poShow .5s ease forwards;animation-delay:1.45s}
        .po-dot{fill:#22e3d4;opacity:0;animation:poShow .5s ease forwards;animation-delay:1.55s}
        .po-head{fill:#eafff9;opacity:0;offset-path:path('M6 32 32 6 58 32 32 58Z');animation:poHead 2s cubic-bezier(.45,0,.2,1) forwards}
        .po-post{fill:#22e3d4;opacity:0;transform-box:fill-box;transform-origin:center;animation:poPop .4s ease forwards}
        .po-post.a{animation-delay:.05s}.po-post.b{animation-delay:.55s}.po-post.c{animation-delay:1.05s}.po-post.d{animation-delay:1.5s}
        @keyframes poDraw{to{stroke-dashoffset:0}}
        @keyframes poHead{0%{offset-distance:0%;opacity:0}6%{opacity:1}86%{offset-distance:100%;opacity:1}100%{offset-distance:100%;opacity:0}}
        @keyframes poShow{to{opacity:1}}
        @keyframes poPop{0%{opacity:0;transform:scale(0)}100%{opacity:1;transform:scale(1)}}
        @media(prefers-reduced-motion:reduce){.po-draw{stroke-dashoffset:0;animation:none}.po-ring,.po-dot,.po-post{opacity:1;animation:none}.po-head{display:none}}
      `}</style></defs>
      <path d="M6 32 32 6 58 32 32 58Z" fill="none" stroke="#2dd4bf" strokeWidth="2.6" opacity=".15" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M32 6V58M6 32H58" fill="none" stroke="#2dd4bf" strokeWidth="1.6" opacity=".24" strokeDasharray="2.5 4.5" strokeLinecap="round" />
      <path className="po-draw" d="M6 32 32 6 58 32 32 58Z" />
      <circle className="po-ring" cx="32" cy="32" r="6" />
      <circle className="po-dot" cx="32" cy="32" r="1.9" />
      <circle className="po-post a" cx="6" cy="32" r="3.4" />
      <circle className="po-post b" cx="32" cy="6" r="3.4" />
      <circle className="po-post c" cx="58" cy="32" r="3.4" />
      <circle className="po-post d" cx="32" cy="58" r="3.4" />
      <circle className="po-head" cx="0" cy="0" r="3.6" />
    </svg>
  );
}

/* looping mark — ONLY for loading states; swap to the static mark when done. */
function LogoMarkLoop({ size = 40 }: any) {
  return (
    <svg className="logo-mark" width={size} height={size} viewBox="0 0 64 64" fill="none" role="img" aria-label="FenceWorks loading">
      <defs><style>{`
        .fw-draw{fill:none;stroke:#22e3d4;stroke-width:3;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:148;stroke-dashoffset:148;animation:fwDraw 3.6s cubic-bezier(.45,0,.2,1) infinite}
        .fw-ring{fill:none;stroke:#22e3d4;stroke-width:3;opacity:0;animation:fwFade 3.6s ease infinite}
        .fw-dot{fill:#22e3d4;opacity:0;animation:fwFade 3.6s ease infinite}
        .fw-head{fill:#eafff9;opacity:0;offset-path:path('M6 32 32 6 58 32 32 58Z');animation:fwHead 3.6s cubic-bezier(.45,0,.2,1) infinite}
        .fw-post{fill:#22e3d4;opacity:0;transform-box:fill-box;transform-origin:center}
        .fw-post.a{animation:fwP1 3.6s ease infinite}.fw-post.b{animation:fwP2 3.6s ease infinite}.fw-post.c{animation:fwP3 3.6s ease infinite}.fw-post.d{animation:fwP4 3.6s ease infinite}
        @keyframes fwDraw{0%{stroke-dashoffset:148}58%{stroke-dashoffset:13}80%{stroke-dashoffset:13}100%{stroke-dashoffset:148}}
        @keyframes fwHead{0%{offset-distance:0%;opacity:0}5%{opacity:1}58%{offset-distance:92%;opacity:1}78%{offset-distance:92%;opacity:0}100%{offset-distance:92%;opacity:0}}
        @keyframes fwFade{0%,32%{opacity:0}46%{opacity:1}80%{opacity:1}90%,100%{opacity:0}}
        @keyframes fwP1{0%,2%{opacity:0;transform:scale(0)}9%{opacity:1;transform:scale(1)}80%{opacity:1}90%,100%{opacity:0}}
        @keyframes fwP2{0%,16%{opacity:0;transform:scale(0)}23%{opacity:1;transform:scale(1)}80%{opacity:1}90%,100%{opacity:0}}
        @keyframes fwP3{0%,31%{opacity:0;transform:scale(0)}38%{opacity:1;transform:scale(1)}80%{opacity:1}90%,100%{opacity:0}}
        @keyframes fwP4{0%,47%{opacity:0;transform:scale(0)}54%{opacity:1;transform:scale(1)}80%{opacity:1}90%,100%{opacity:0}}
        @media(prefers-reduced-motion:reduce){.fw-draw,.fw-ring,.fw-dot,.fw-post{animation:none;opacity:1;stroke-dashoffset:0}.fw-head{display:none}}
      `}</style></defs>
      <path d="M6 32 32 6 58 32 32 58Z" fill="none" stroke="#2dd4bf" strokeWidth="2.6" opacity=".15" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M32 6V58M6 32H58" fill="none" stroke="#2dd4bf" strokeWidth="1.6" opacity=".24" strokeDasharray="2.5 4.5" strokeLinecap="round" />
      <path className="fw-draw" d="M6 32 32 6 58 32 32 58Z" />
      <circle className="fw-ring" cx="32" cy="32" r="6" />
      <circle className="fw-dot" cx="32" cy="32" r="1.9" />
      <circle className="fw-post a" cx="6" cy="32" r="3.4" />
      <circle className="fw-post b" cx="32" cy="6" r="3.4" />
      <circle className="fw-post c" cx="58" cy="32" r="3.4" />
      <circle className="fw-post d" cx="32" cy="58" r="3.4" />
      <circle className="fw-head" cx="0" cy="0" r="3.6" />
    </svg>
  );
}

/* ---- icon set (lucide-ish, stroke) ---- */
const ICON_PATHS = {
  dashboard: 'M3 3h8v8H3zM13 3h8v5h-8zM13 11h8v10h-8zM3 13h8v8H3z',
  image: 'M4 4h16v16H4zM8.5 10.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM20 15l-5-5L5 20',
  map: 'M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14',
  box: 'M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8',
  orbit: 'M4 12.5c0-2.3 3.6-4.2 8-4.2s8 1.9 8 4.2-3.6 4.2-8 4.2c-1.6 0-3.1-.3-4.3-.7M9.4 17.9l-2-1.2 1.3-1.9M12 4v9',
  gear: 'M12 9a3 3 0 100 6 3 3 0 000-6zM19.4 15a1.6 1.6 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.6 1.6 0 00-2.7 1.1V21a2 2 0 11-4 0v-.1A1.6 1.6 0 005 19.4l-.1.1a2 2 0 11-2.8-2.8l.1-.1A1.6 1.6 0 003 13.9H3a2 2 0 110-4h.1A1.6 1.6 0 004.6 7l-.1-.1a2 2 0 112.8-2.8l.1.1A1.6 1.6 0 009 4.6h.1A1.6 1.6 0 0011 3.1V3a2 2 0 014 0v.1A1.6 1.6 0 0016.7 4.6l.1-.1a2 2 0 112.8 2.8l-.1.1a1.6 1.6 0 001.1 2.7H21a2 2 0 010 4h-.1a1.6 1.6 0 00-1.5 1z',
  clipboard: 'M9 4h6a1 1 0 011 1v1h2a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h2V5a1 1 0 011-1zM9 4v3h6V4',
  book: 'M4 5a2 2 0 012-2h13v16H6a2 2 0 00-2 2zM4 5v14',
  help: 'M12 22a10 10 0 100-20 10 10 0 000 20zM9.1 9a3 3 0 015.8 1c0 2-3 3-3 3M12 17h.01',
  chevdown: 'M6 9l6 6 6-6',
  menu: 'M4 6h16M4 12h16M4 18h16',
  chevright: 'M9 6l6 6-6 6',
  plus: 'M12 5v14M5 12h14',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
  chat: 'M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z',
  check: 'M20 6 9 17l-5-5',
  x: 'M18 6 6 18M6 6l12 12',
  dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
  activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
  building: 'M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 13v.01M9 17v.01',
  search: 'M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.3-4.3',
  edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.1 2.1 0 013 3L12 15l-4 1 1-4z',
  printer: 'M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z',
  pen: 'M12 19l7-7 3 3-7 7-3-3zM18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5zM2 2l7.6 7.6',
  lock: 'M5 11h14a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1v-8a1 1 0 011-1zM8 11V7a4 4 0 018 0v4',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  target: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 17a5 5 0 100-10 5 5 0 000 10zM12 13a1 1 0 100-2 1 1 0 000 2z',
  trash: 'M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2M19 6l-1 14a1 1 0 01-1 1H7a1 1 0 01-1-1L5 6M10 11v6M14 11v6',
  undo: 'M3 7v6h6M3 13a9 9 0 1 0 3-7.7L3 8',
  layers: 'M12 2 2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  bolt: 'M13 2 3 14h7l-1 8 10-12h-7z',
  bell: 'M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0',
  user: 'M20 21a8 8 0 10-16 0M12 11a4 4 0 100-8 4 4 0 000 8z',
  ruler: 'M3 9l12 12 6-6L9 3zM7 9l2 2M11 5l2 2M9 13l2 2M13 9l2 2',
  calendar: 'M8 2v4M16 2v4M3 8h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z',
  clock: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 7v5l3 2',
  mappin: 'M12 22s8-5.5 8-12a8 8 0 10-16 0c0 6.5 8 12 8 12zM12 13a3 3 0 100-6 3 3 0 000 6z',
  phone: 'M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012 4.2 2 2 0 014 2h3a2 2 0 012 1.7c.1 1 .4 1.9.7 2.8a2 2 0 01-.5 2.1L8 10a16 16 0 006 6l1.4-1.2a2 2 0 012.1-.5c.9.3 1.8.6 2.8.7A2 2 0 0122 16.9z',
  mail: 'M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zM22 6l-10 7L2 6',
  download: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3',
  upload: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12',
  sliders: 'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6',
};
function Icon({ name, size = 18, className = "", style }: any) {
  const d = ICON_PATHS[name];
  if (!d) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
      {d.split("M").filter(Boolean).map((seg, k) => <path key={k} d={"M" + seg} />)}
    </svg>
  );
}

/* ---- progress ring ---- */
function Ring({ value = 0, size = 92, stroke = 8, color = "var(--teal)", track = "var(--line-2)", children }: any) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, value)) / 100) * c;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke={track} strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset .8s cubic-bezier(.2,.7,.3,1)" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>{children}</div>
    </div>
  );
}


/* ---- fence profile artwork (specs + codes cards) ---- */
function FenceArt({ type = "wood", color, width = 240, height = 150 }: any) {
  const W = width, H = height, gy = H - 26; // ground line
  const ground = "#e7dcc4";
  const woodFill = color && type === "wood" ? color : "#c79356";
  const px = [W * 0.18, W * 0.82]; // post x positions
  const railY1 = gy - (gy * 0.55), railY2 = gy - (gy * 0.18);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
      <rect x="0" y={gy} width={W} height={H - gy + 6} fill={ground} />
      <rect x="0" y={gy} width={W} height="2.5" fill="rgba(0,0,0,0.12)" />
      {/* posts in ground */}
      {px.map((x, i) => <rect key={i} x={x - 5} y={gy} width="10" height="20" fill="rgba(0,0,0,0.22)" />)}

      {type === "wood" && (() => {
        const top = gy * 0.18, n = 13, gap = (px[1] - px[0]) / n;
        return <g>
          {Array.from({ length: n + 1 }).map((_, i) => {
            const x = px[0] + i * gap;
            return <path key={i} d={`M${x} ${top + 8} L${x} ${gy} L${x + gap - 2} ${gy} L${x + gap - 2} ${top + 8} L${x + (gap - 2) / 2} ${top} Z`}
              fill={woodFill} stroke="rgba(0,0,0,0.10)" strokeWidth="0.7" />;
          })}
          {[px[0] - 7, px[1] - 3].map((x, i) => <rect key={i} x={x} y={top} width="10" height={gy - top} fill="#a9763f" />)}
        </g>;
      })()}

      {type === "vinyl" && (() => {
        const top = gy * 0.2;
        return <g>
          <rect x={px[0]} y={top + 6} width={px[1] - px[0]} height={gy - top - 6} fill={color || "#f4f6f9"} stroke="#d4dae2" />
          <rect x={px[0]} y={top + 18} width={px[1] - px[0]} height="6" fill="#e3e8ee" />
          <rect x={px[0]} y={gy - 24} width={px[1] - px[0]} height="6" fill="#e3e8ee" />
          {px.map((x, i) => <g key={i}><rect x={x - 8} y={top} width="16" height={gy - top} fill="#fbfcfd" stroke="#d4dae2" /><rect x={x - 8} y={top - 7} width="16" height="8" rx="2" fill="#eef1f5" /></g>)}
        </g>;
      })()}

      {type === "iron" && (() => {
        const top = gy * 0.22, n = 18, gap = (px[1] - px[0]) / n, c = color || "#2c333d";
        return <g>
          <rect x={px[0]} y={railY1} width={px[1] - px[0]} height="5" fill={c} />
          <rect x={px[0]} y={railY2} width={px[1] - px[0]} height="5" fill={c} />
          {Array.from({ length: n }).map((_, i) => {
            const x = px[0] + 6 + i * gap;
            return <rect key={i} x={x} y={top} width="2.6" height={gy - top} fill={c} />;
          })}
          {px.map((x, i) => <rect key={i} x={x - 4} y={top - 6} width="8" height={gy - top + 6} fill="#1c222b" />)}
        </g>;
      })()}

      {(type === "chain" || type === "industrial") && (() => {
        const top = gy * 0.24, c = "#9aa6b8";
        return <g>
          <defs><pattern id={"mesh" + W + H} width="11" height="11" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <path d="M0 0H11M0 0V11" stroke="#8c98aa" strokeWidth="1.4" /></pattern></defs>
          <rect x={px[0]} y={top + 4} width={px[1] - px[0]} height={gy - top - 4} fill={`url(#mesh${W}${H})`} opacity="0.85" />
          <rect x={px[0]} y={top} width={px[1] - px[0]} height="4" fill={c} />
          {px.map((x, i) => <rect key={i} x={x - 3} y={top - 4} width="6" height={gy - top + 4} fill={c} />)}
        </g>;
      })()}

      {type === "rail" && (() => {
        const top = gy * 0.3, n = 12, gap = (px[1] - px[0]) / n, c = color || "#b9b1a4";
        return <g>
          <rect x={W * 0.05} y={gy - 4} width={W * 0.9} height="22" fill="#cdbf9f" />
          <rect x={px[0] - 10} y={top} width={px[1] - px[0] + 20} height="9" rx="2" fill={c} />
          <rect x={px[0]} y={gy - 18} width={px[1] - px[0]} height="6" fill={c} />
          {Array.from({ length: n }).map((_, i) => <rect key={i} x={px[0] + 6 + i * gap} y={top + 9} width="3" height={gy - top - 27} fill={c} />)}
        </g>;
      })()}

      {type === "stairs" && (() => {
        const steps = 5, sw = (W * 0.62) / steps, sh = (gy - 8) / steps;
        return <g>
          {Array.from({ length: steps }).map((_, i) => (
            <rect key={i} x={W * 0.18 + i * sw} y={gy - (i + 1) * sh} width={sw + 2} height={sh} fill="#c79356" stroke="rgba(0,0,0,0.12)" />
          ))}
        </g>;
      })()}
    </svg>
  );
}

/* branded logo: the contractor's uploaded logo when set, else the LogoMark.
   Used on quotes, the customer hub, and invoices. */
function CompanyLogo({ company, size = 44 }: any) {
  const url = company && company.logo;
  if (url) return <img src={url} alt={(company && company.name) || "Logo"} style={{ height: size, maxWidth: size * 3.2, objectFit: "contain", display: "block" }} />;
  return <LogoMark size={size} />;
}

/* ---- "Powered by Fence Works" — a Free contractor's customer-facing pages ----
   Whether it shows is the service's call (server/src/poweredby.js, from the
   workspace's subscription): the hub payload's `poweredBy`, or the plan sync's
   for the printed quote — never settings.plan. The sandbox has no subscription
   and follows its own plan (Growth → none). No answer is no badge. */
function poweredByShown(flag: any, sandbox?: boolean, plan?: any) {
  return sandbox ? plan === "free" : flag === true;
}
function poweredByUrl(medium: string) {
  return "https://fenceworks.ai/?utm_source=powered_by&utm_medium=" + medium + "&utm_campaign=free_badge";
}
function PoweredBy({ medium }: any) {
  return (
    <div className="powered-by-row">
      <a className="powered-by" href={poweredByUrl(medium)} target="_blank" rel="noopener">
        <span aria-hidden="true"><LogoMark size={14} variant="final" /></span>Estimate powered by <b>Fence Works</b> <span>— software for fence contractors</span>
      </a>
    </div>
  );
}

/* ---- toasts: the one non-blocking notice rail ----
   notify("Saved") from anywhere — React screens import it, non-React code
   (lib/*.ts) calls window.VFP_NOTIFY. Replaces the blocking alert() calls
   that used to interrupt every save / send / connect. Kind is inferred from
   the wording unless given; errors stick around longer. */
type ToastKind = "info" | "success" | "error";
const toastListeners = new Set<(t: any) => void>();
function guessKind(text: string): ToastKind {
  const s = String(text || "").toLowerCase();
  if (/fail|error|couldn|can['’]t|cannot|invalid|denied|unable|not (found|allowed|available)|wrong|declin|expired|limit reached|no longer|missing|blocked/.test(s)) return "error";
  if (/saved|sent|done|connected|copied|created|updated|success|released|posted|added|removed|✓/.test(s)) return "success";
  return "info";
}
function notify(text: string, opts?: { kind?: ToastKind; ms?: number }) {
  const t = { id: "t" + Date.now() + Math.random().toString(36).slice(2, 6), text: String(text || ""), kind: (opts && opts.kind) || guessKind(text), ms: (opts && opts.ms) || 0 };
  if (!toastListeners.size) { try { console[t.kind === "error" ? "warn" : "log"]("[notify]", t.text); } catch (e) {} }
  toastListeners.forEach((fn) => fn(t));
  return t.id;
}
function Toaster() {
  const [items, setItems] = React.useState<any[]>([]);
  React.useEffect(() => {
    const on = (t: any) => {
      setItems((xs) => [...xs.filter((x) => x.text !== t.text), t].slice(-4));   // de-dupe identical repeats
      const ms = t.ms || (t.kind === "error" ? 8000 : 4200);
      setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== t.id)), ms);
    };
    toastListeners.add(on);
    return () => { toastListeners.delete(on); };
  }, []);
  if (!items.length) return null;
  return (
    <Portal>
      <div className="toast-rail" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={"toast toast-" + t.kind}>
            <Icon name={t.kind === "error" ? "x" : t.kind === "success" ? "check" : "bell"} size={15} />
            <span style={{ flex: 1, minWidth: 0 }}>{t.text}</span>
            <button className="toast-x" aria-label="Dismiss" onClick={() => setItems((xs) => xs.filter((x) => x.id !== t.id))}>×</button>
          </div>
        ))}
      </div>
    </Portal>
  );
}
(window as any).VFP_NOTIFY = notify;

/* ---- confirm: the one in-app yes/no dialog ----
   `if (!(await confirmDialog({ title, body, confirmLabel, danger }))) return;`
   replaces every blocking browser confirm prompt. Same listener pattern as the
   toasts: React screens import it, non-React code calls window.VFP_CONFIRM.
   With no ConfirmHost mounted it falls back to window.confirm so behaviour
   never silently changes. One dialog at a time — extra requests queue. */
/* `requireText` turns the dialog into a typed confirmation: Confirm stays
   disabled until the exact phrase is typed. For anything that destroys work a
   second "are you sure?" just gets clicked through — making someone type the
   customer's name is the difference between a slip and a decision. */
type ConfirmOpts = { title: string; body?: string; confirmLabel?: string; cancelLabel?: string; danger?: boolean; requireText?: string; requireHint?: any };
const confirmListeners = new Set<(req: any) => void>();
function confirmDialog(opts: ConfirmOpts): Promise<boolean> {
  const o: any = opts || {};
  if (!confirmListeners.size) return Promise.resolve(window.confirm(String(o.title || "") + (o.body ? "\n\n" + o.body : "")));
  return new Promise<boolean>((resolve) => {
    const req = { ...o, id: "c" + Date.now() + Math.random().toString(36).slice(2, 6), resolve };
    confirmListeners.forEach((fn) => fn(req));
  });
}
function ConfirmHost() {
  const [queue, setQueue] = React.useState<any[]>([]);
  const cur = queue[0] || null;
  const okRef = React.useRef<any>(null), cancelRef = React.useRef<any>(null);
  const [typed, setTyped] = React.useState<any>("");
  React.useEffect(() => {
    const on = (req: any) => setQueue((xs) => [...xs, req]);
    confirmListeners.add(on);
    return () => { confirmListeners.delete(on); };
  }, []);
  // focus: Cancel for destructive asks (a stray Enter can't delete anything),
  // else Confirm; back to wherever it came from when the dialog closes.
  React.useEffect(() => { setTyped(""); }, [cur && cur.id]);
  React.useEffect(() => {
    if (!cur) return;
    const prev: any = document.activeElement;
    const btn = cur.danger ? cancelRef.current : okRef.current;
    if (btn) btn.focus({ preventScroll: true });
    return () => { if (prev && prev.isConnected && prev.focus) { try { prev.focus({ preventScroll: true }); } catch (e) {} } };
  }, [cur && cur.id]);
  if (!cur) return null;
  const done = (ok: boolean) => { setQueue((xs) => xs.filter((x) => x.id !== cur.id)); cur.resolve(!!ok); };
  const paras = String(cur.body || "").split(/\n+/).filter((s) => s.trim());
  const need = String(cur.requireText || "");
  const ready = !need || typed.trim().toLowerCase() === need.trim().toLowerCase();
  return (
    <Portal>
      <div className="modal-scrim confirm-scrim" data-dialog onClick={(e: any) => { if (e.target === e.currentTarget) done(false); }}
        onKeyDown={(e: any) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); done(false); } }}>
        <div className="modal confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="confirm-dlg-title" aria-describedby={paras.length ? "confirm-dlg-body" : undefined}>
          <h3 id="confirm-dlg-title" className="display">{cur.title}</h3>
          {paras.length > 0 && <div id="confirm-dlg-body">{paras.map((p, i) => <p key={i}>{p}</p>)}</div>}
          {need && (
            <label style={{ display: "block", marginTop: 4 }}>
              <span className="field-label">{cur.requireHint || <>Type <b style={{ color: "var(--fg)" }}>{need}</b> to confirm</>}</span>
              <input className="input" autoFocus value={typed} onChange={(e: any) => setTyped(e.target.value)}
                onKeyDown={(e: any) => { if (e.key === "Enter" && ready) { e.preventDefault(); done(true); } }} placeholder={need} />
            </label>
          )}
          <div className="confirm-actions">
            <button type="button" ref={cancelRef} className="btn btn-ghost" onClick={() => done(false)}>{cur.cancelLabel || "Cancel"}</button>
            <button type="button" ref={okRef} disabled={!ready} className={"btn " + (cur.danger ? "btn-danger" : "btn-primary")} onClick={() => ready && done(true)}>{cur.confirmLabel || "Confirm"}</button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
(window as any).VFP_CONFIRM = confirmDialog;

/* ---- dialog a11y: one mechanism for every overlay ----
   No per-modal code. A MutationObserver on <body> spots `.modal-scrim` /
   `[data-dialog]` backdrops as React portals them in and keeps a stack of the
   open ones. On open the panel (first `.modal`, else the backdrop itself) gets
   role="dialog" / aria-modal / aria-labelledby from its heading, and focus
   moves inside. While any are open ONE capture-phase keydown handler traps Tab
   in the top panel and turns Escape into a click on the backdrop, so the
   component's own scrim onClick closes it exactly as a real click would.
   When the backdrop leaves the DOM, focus returns to where it was.
   Opt-outs: data-no-dialog (never a dialog), data-no-escape (Escape ignored). */
const DIALOG_SEL = ".modal-scrim, [data-dialog]";
const FOCUSABLE_SEL = 'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])';
let dialogA11yOn = false, dialogIds = 0;
function installDialogA11y() {
  if (dialogA11yOn || typeof document === "undefined" || typeof MutationObserver === "undefined") return;
  dialogA11yOn = true;
  const stack: { el: Element; panel: HTMLElement; prev: any }[] = [];
  const usable = (el: any) => !el.disabled && !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  const focusables = (panel: Element) => Array.from(panel.querySelectorAll(FOCUSABLE_SEL)).filter(usable) as HTMLElement[];
  const focus = (el: any) => { if (el && el.focus) { try { el.focus({ preventScroll: true }); } catch (e) {} } };
  const onKey = (e: KeyboardEvent) => {
    const top = stack[stack.length - 1];
    if (!top) return;
    if (e.key === "Escape") {
      if (top.el.hasAttribute("data-no-escape")) return;
      // close via the backdrop-click path (React flushes a discrete click synchronously, so the
      // overlay is already gone here when that path closes it). If the backdrop has no close
      // handler the overlay is still connected — leave the key alone so the component's own
      // Escape listener keeps working instead of swallowing it.
      top.el.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
      if (!top.el.isConnected) { e.preventDefault(); e.stopPropagation(); }
    } else if (e.key === "Tab") {
      const list = focusables(top.panel), cur: any = document.activeElement, inside = top.panel.contains(cur);
      if (!list.length) { e.preventDefault(); focus(top.panel); }
      else if (!inside || cur === (e.shiftKey ? list[0] : list[list.length - 1])) { e.preventDefault(); focus(e.shiftKey ? list[list.length - 1] : list[0]); }
    }
  };
  const open = (el: Element) => {
    if (!el.isConnected || stack.some((s) => s.el === el) || el.closest(".toast-rail, [data-no-dialog]")) return;
    const panel = (el.querySelector(".modal") || el) as HTMLElement;
    if (!panel.getAttribute("role")) panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "true");
    if (!panel.hasAttribute("aria-label") && !panel.hasAttribute("aria-labelledby")) {
      const h = panel.querySelector("h1, h2, h3, h4");
      if (h) { if (!h.id) h.id = "dlg-" + (++dialogIds); panel.setAttribute("aria-labelledby", h.id); }
    }
    if (!stack.length) document.addEventListener("keydown", onKey, true);
    stack.push({ el, panel, prev: document.activeElement });
    if (panel.contains(document.activeElement)) return;
    const first = (panel.querySelector("[autofocus]") as HTMLElement) || focusables(panel)[0];
    if (first) focus(first);
    else { if (!panel.hasAttribute("tabindex")) panel.setAttribute("tabindex", "-1"); focus(panel); }
  };
  new MutationObserver((muts) => {
    let removed = false;
    const added: Element[] = [];
    for (const m of muts) {
      if (m.removedNodes.length) removed = true;
      m.addedNodes.forEach((n: any) => {
        if (n.nodeType !== 1) return;
        if (n.matches(DIALOG_SEL)) added.push(n);
        n.querySelectorAll(DIALOG_SEL).forEach((d: Element) => added.push(d));
      });
    }
    if (removed) for (let i = stack.length - 1; i >= 0; i--) {
      if (stack[i].el.isConnected) continue;
      const gone = stack.splice(i, 1)[0];
      if (gone.prev && gone.prev.isConnected) focus(gone.prev);
      if (!stack.length) document.removeEventListener("keydown", onKey, true);
    }
    added.forEach(open);
  }).observe(document.body, { childList: true, subtree: true });
}

/* ---- DataTable: the one dense list ----
   Thin wrapper over the `.rep-src-table` look every collection screen now
   shares (Products, Estimates, POs, Inventory). Columns declare a key, label,
   `num` (right-aligned, `th.num`), an optional `render(row)` and an optional
   `sort(row)` value getter (`sort: false` makes the column unsortable).
   Clicking a header toggles asc/desc; `initialSort` seeds it. Rows can be
   click-through (`onRowClick` — pointer cursor, Enter/Space from the
   keyboard) and clicks that start on a button / input inside a cell never
   open the row, so inline actions need no stopPropagation. The wrapper
   scrolls sideways on phones instead of widening the page. */
type DataCol = {
  key: string; label?: any; num?: boolean; render?: (row: any, i: number) => any;
  sort?: false | ((row: any) => any); width?: any; className?: string; title?: string;
};
const INTERACTIVE_SEL = "button, a, input, select, textarea, label, [data-no-row]";
function dtValue(col: DataCol, row: any) {
  if (typeof col.sort === "function") return col.sort(row);
  return row ? row[col.key] : undefined;
}
function dtCompare(a: any, b: any) {
  const an = a == null || a === "", bn = b == null || b === "";
  if (an && bn) return 0; if (an) return 1; if (bn) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return (a ? 1 : 0) - (b ? 1 : 0);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}
function DataTable({ columns, rows, rowKey, onRowClick, sort = true, initialSort, empty = null, dense = false, className = "", rowClass, style }: any) {
  const cols: DataCol[] = columns || [];
  const [sortKey, setSortKey] = React.useState<any>(initialSort ? initialSort.key : null);
  const [dir, setDir] = React.useState<any>(initialSort && initialSort.dir === "desc" ? "desc" : "asc");
  const canSort = (c: DataCol) => sort !== false && c.sort !== false;
  const toggle = (c: DataCol) => {
    if (!canSort(c)) return;
    if (sortKey === c.key) setDir((d: any) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(c.key); setDir(c.num ? "desc" : "asc"); }
  };
  const list = React.useMemo(() => {
    const src = rows || [];
    const c = cols.find((x) => x.key === sortKey);
    if (!c || !canSort(c)) return src;
    const idx = src.map((r: any, i: number) => ({ r, i, v: dtValue(c, r) }));
    idx.sort((x: any, y: any) => (dtCompare(x.v, y.v) || x.i - y.i) * (dir === "desc" ? -1 : 1));
    return idx.map((x: any) => x.r);
  }, [rows, cols, sortKey, dir, sort]);
  const keyOf = (r: any, i: number) => (typeof rowKey === "function" ? rowKey(r, i) : rowKey ? r[rowKey] : r.id != null ? r.id : i);
  if (!list.length) return empty;
  return (
    <div className={"dt-wrap " + className} style={style}>
      <table className={"rep-src-table dt" + (dense ? " dense" : "") + (onRowClick ? " clickable" : "")}>
        <thead>
          <tr>
            {cols.map((c) => {
              const on = sortKey === c.key;
              return (
                <th key={c.key} className={(c.num ? "num " : "") + (canSort(c) ? "sortable " : "") + (on ? "sorted" : "")}
                  style={c.width ? { width: c.width } : undefined} title={c.title}
                  aria-sort={on ? (dir === "asc" ? "ascending" : "descending") : undefined}>
                  {/* a real button, so the column can be sorted from the
                      keyboard. A bare onClick on the <th> could only be reached
                      with a mouse. */}
                  {canSort(c)
                    ? <button type="button" className="dt-sort" onClick={() => toggle(c)}
                        aria-label={`Sort by ${c.label != null ? c.label : c.key}` + (on ? (dir === "asc" ? ", currently ascending" : ", currently descending") : "")}>
                        {c.label != null ? c.label : c.key}
                        <span className="dt-arrow" aria-hidden="true">{on ? (dir === "asc" ? "▲" : "▼") : "△"}</span>
                      </button>
                    : (c.label != null ? c.label : c.key)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {list.map((r: any, i: number) => {
            const open = onRowClick ? (e: any) => { if (e.target && e.target.closest && e.target.closest(INTERACTIVE_SEL)) return; onRowClick(r, e); } : undefined;
            return (
              <tr key={keyOf(r, i)} className={(onRowClick ? "dt-click " : "") + ((rowClass && rowClass(r)) || "")}
                role={onRowClick ? "button" : undefined} tabIndex={onRowClick ? 0 : undefined}
                onClick={open}
                onKeyDown={onRowClick ? (e: any) => { if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) { e.preventDefault(); onRowClick(r, e); } } : undefined}>
                {cols.map((c) => <td key={c.key} className={(c.num ? "num tnum " : "") + (c.className || "")}>{c.render ? c.render(r, i) : (r[c.key] != null ? r[c.key] : "—")}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---- useLocalPref: a per-device UI preference (view toggle, default tab)
   remembered in localStorage; never synced, never load-bearing. */
/* ---- useNarrowScreen: true while the viewport is at most `maxWidth` px.
   For screens that change SHAPE on a phone (list → detail instead of
   side-by-side), not just reflow — CSS can hide a pane, but which pane is
   the user's choice, and that lives in state. */
function useNarrowScreen(maxWidth = 980) {
  const get = () => { try { return window.matchMedia(`(max-width: ${maxWidth}px)`).matches; } catch (e) { return false; } };
  const [narrow, setNarrow] = React.useState<any>(get);
  React.useEffect(() => {
    let mq: any; try { mq = window.matchMedia(`(max-width: ${maxWidth}px)`); } catch (e) { return; }
    const on = () => setNarrow(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener("change", on); else if (mq.addListener) mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", on); else if (mq.removeListener) mq.removeListener(on); };
  }, [maxWidth]);
  return narrow;
}

function useLocalPref(key: string, initial: any) {
  const [v, setV] = React.useState<any>(() => { try { const x = localStorage.getItem(key); return x != null && x !== "" ? x : initial; } catch (e) { return initial; } });
  // set(v, { remember: false }) switches the view without changing the remembered default (deep links, transient jumps)
  const set = React.useCallback((nv: any, opts?: { remember?: boolean }) => { setV(nv); if (opts && opts.remember === false) return; try { localStorage.setItem(key, String(nv)); } catch (e) {} }, [key]);
  return [v, set];
}

/* ---- ListRow: a click-through row ----
   Thin wrapper over `.proj-row` for collections that are lists of things
   with a leading slot (switch, avatar, logo dot), a title + muted sub line,
   and trailing cells (chips, amounts, buttons, chevron). Trailing cells wrap
   under the title on a phone. Same interactive-element guard as DataTable,
   so a switch or button inside the row never opens it. */
function ListRow({ leading, title, sub, trailing, onClick, className = "", muted = false, style, children, ariaLabel }: any) {
  const open = onClick ? (e: any) => { if (e.target && e.target.closest && e.target.closest(INTERACTIVE_SEL)) return; onClick(e); } : undefined;
  return (
    <div className={"proj-row list-row" + (onClick ? " click" : "") + (muted ? " muted-row" : "") + (className ? " " + className : "")} style={style}
      role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined} aria-label={ariaLabel}
      onClick={open}
      onKeyDown={onClick ? (e: any) => { if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) { e.preventDefault(); onClick(e); } } : undefined}>
      {leading != null && <div className="list-row-lead">{leading}</div>}
      <div className="list-row-main">
        <div className="list-row-title">{title}</div>
        {sub != null && sub !== "" && <div className="list-row-sub">{sub}</div>}
        {children}
      </div>
      {trailing != null && <div className="list-row-trail">{trailing}</div>}
    </div>
  );
}

/* ---- "Add to home screen" nudge (PWA install) ----
   Chrome / Edge / Android fire `beforeinstallprompt` once the manifest and the
   service worker qualify. We hold that event and offer it from a slim card,
   once: "Not now" keeps quiet for 30 days, an accepted install (or an app
   already running standalone) never shows it again. iOS has no prompt API,
   so it simply never appears there. */
const INSTALL_KEY = "fw_install_dismissed";
/* The browser fires `beforeinstallprompt` once per page load, to whoever is
   listening at that moment. Hold it at module level so any screen can offer
   the install later (the nudge, Crew Mode's button), not only whichever
   component happened to mount first. */
let installEvt: any = null;
const isStandalone = () => { try { return !!(window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || (navigator as any).standalone === true; } catch (e) { return false; } };
try {
  window.addEventListener("beforeinstallprompt", (e: any) => { e.preventDefault(); installEvt = e; window.dispatchEvent(new Event("fw:install-ready")); });
  window.addEventListener("appinstalled", () => { installEvt = null; window.dispatchEvent(new Event("fw:install-ready")); });
} catch (e) {}
/* { canPrompt, standalone, ios, install() } — `ios` means Safari on iPhone/iPad,
   which has no prompt API: the UI shows the Share → Add to Home Screen hint. */
function useInstallPrompt() {
  const [, bump] = React.useState<any>(0);
  React.useEffect(() => { const h = () => bump((n: number) => n + 1); window.addEventListener("fw:install-ready", h); return () => window.removeEventListener("fw:install-ready", h); }, []);
  const standalone = isStandalone();
  const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent) && !(window as any).MSStream;
  const install = async () => {
    const evt = installEvt; if (!evt) return false;
    try { evt.prompt(); const r = await evt.userChoice; installEvt = null; if (r && r.outcome === "accepted") { notify("Fence Works is on your home screen"); return true; } } catch (e) {}
    return false;
  };
  return { canPrompt: !!installEvt && !standalone, standalone, ios: ios && !standalone, install };
}
function InstallNudge() {
  const [evt, setEvt] = React.useState<any>(null);
  React.useEffect(() => {
    if (isStandalone()) return;
    try { const t = Number(localStorage.getItem(INSTALL_KEY) || 0); if (t && Date.now() - t < 30 * 86400000) return; } catch (e) {}
    const on = () => setEvt(installEvt);
    on();
    window.addEventListener("fw:install-ready", on);
    return () => window.removeEventListener("fw:install-ready", on);
  }, []);
  if (!evt) return null;
  const later = () => { try { localStorage.setItem(INSTALL_KEY, String(Date.now())); } catch (e) {} setEvt(null); };
  const add = async () => {
    try { evt.prompt(); const r = await evt.userChoice; installEvt = null; if (r && r.outcome === "accepted") notify("Fence Works is on your home screen"); } catch (e) {}
    later();
  };
  return (
    <Portal>
      <div className="install-nudge" role="region" aria-label="Add to home screen" data-no-dialog>
        <Icon name="bolt" size={16} />
        <div style={{ flex: 1, minWidth: 0 }}><b>Add Fence Works to your home screen.</b><span> Opens like an app and works in the truck with no signal.</span></div>
        <button className="btn btn-primary btn-sm" onClick={add}>Add</button>
        <button className="toast-x" aria-label="Not now" onClick={later}>×</button>
      </div>
    </Portal>
  );
}

Object.assign(window, { LogoMark, LogoMarkOnce, LogoMarkLoop, Icon, Ring, FenceArt, CompanyLogo, Portal, Toaster, notify, ConfirmHost, confirmDialog, installDialogA11y, InstallNudge, DataTable, ListRow, useLocalPref });

/* useDragPosition — make a fixed panel movable by a handle and remember where
   it was put (localStorage). Returns `style` for the panel (undefined until it
   has been moved, so the CSS default position applies), `handleProps` for the
   grip/header, `moved` and `reset()`. Drags never start on buttons, links or
   inputs inside the handle, so a header full of controls still works. Clamps
   to the viewport, including after a resize. */
function useDragPosition(key: string, opts: { disabled?: boolean } = {}) {
  const read = () => { try { const v = JSON.parse(localStorage.getItem(key) || "null"); return v && isFinite(v.x) && isFinite(v.y) ? v : null; } catch (e) { return null; } };
  const [pos, setPos] = React.useState<any>(read);
  const elRef = React.useRef<any>(null);
  const clamp = (p: any, w: number, h: number) => ({ x: Math.max(8, Math.min(window.innerWidth - w - 8, p.x)), y: Math.max(8, Math.min(window.innerHeight - h - 8, p.y)) });
  React.useEffect(() => {
    const onResize = () => setPos((p: any) => { if (!p || !elRef.current) return p; const r = elRef.current.getBoundingClientRect(); return clamp(p, r.width, r.height); });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const onPointerDown = (e: any) => {
    if (opts.disabled) return;
    if (e.button != null && e.button !== 0) return;
    if (e.target && e.target.closest && e.target.closest("button, a, input, select, textarea, label")) return;
    const panel = elRef.current || (e.currentTarget && e.currentTarget.closest && e.currentTarget.closest("[data-drag-panel]"));
    if (!panel) return;
    elRef.current = panel;
    const r = panel.getBoundingClientRect();
    const start = { mx: e.clientX, my: e.clientY, x: r.left, y: r.top, w: r.width, h: r.height };
    let moved = false;
    const move = (ev: any) => {
      const dx = ev.clientX - start.mx, dy = ev.clientY - start.my;
      if (!moved && Math.abs(dx) + Math.abs(dy) < 4) return;
      moved = true;
      setPos(clamp({ x: start.x + dx, y: start.y + dy }, start.w, start.h));
    };
    const up = () => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up);
      if (moved) setPos((p: any) => { try { localStorage.setItem(key, JSON.stringify(p)); } catch (x) {} return p; });
    };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", up);
    e.preventDefault();
  };
  const reset = () => { setPos(null); try { localStorage.removeItem(key); } catch (e) {} };
  const style = pos && !opts.disabled ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto", animation: "none" } : undefined;
  return { style, moved: !!pos && !opts.disabled, reset, handleProps: { onPointerDown, style: { cursor: opts.disabled ? undefined : "grab", touchAction: "none" } }, panelRef: elRef };
}

export { clickable, useNarrowScreen, useInstallPrompt, SampleJobPreview, useDragPosition, LogoMark, LogoMarkOnce, LogoMarkLoop, Icon, Ring, FenceArt, CompanyLogo, PoweredBy, poweredByShown, Portal, ICON_PATHS, Toaster, notify, ConfirmHost, confirmDialog, installDialogA11y, InstallNudge, DataTable, ListRow, useLocalPref };

/* ---- clickable(): make a non-button behave like the button it claims to be --
   Card rows across the app carried role="button" tabIndex={0} onClick={...} and
   no key handler. That is worse than not being focusable: a keyboard or screen
   reader user tabs to something announced as a button, presses Enter, and
   nothing happens. Spread this instead — it adds Enter/Space, and ignores keys
   that came from a control INSIDE the row so the buttons in it still work. */
function clickable(onActivate: (e: any) => void, opts?: { label?: string; disabled?: boolean }) {
  if (opts && opts.disabled) return {} as any;
  return {
    role: "button",
    tabIndex: 0,
    "aria-label": (opts && opts.label) || undefined,
    onClick: onActivate,
    onKeyDown: (e: any) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      if (e.target !== e.currentTarget) return;
      e.preventDefault();
      onActivate(e);
    },
  } as any;
}

/* ---- SampleJobPreview (PROD-03) ----------------------------------------
   The one price preview. Wherever a product's price is shown before a real
   estimate exists — product editor, catalog, settings, quick-add — it is this
   component over lib/samplejob's `sampleJob()`, which runs the real engine.

   Three pages used to compute this three different ways and disagree by $6/ft
   on the same product. The assumptions now sit next to the number instead of
   in a tooltip, and markup and margin are printed as the different things they
   are. `onInputs` makes the sample editable where that helps; without it the
   assumptions are read-only. */
function SampleJobPreview({ product, settings, inputs, onInputs, compact, title }: any) {
  const job = sampleJob(product, settings, inputs);
  const money = (window as any).VFP.money0;
  if (!job.ok) return <div className="sample-job empty">Add a cost below to price a sample job.</div>;
  const rows: any[] = [
    ["Materials", job.materials, "what you pay your supplier"],
    ["Labor", job.labor, `${job.laborHours.toFixed(1)} crew-hours at your crew rate`],
    ["Markup", job.markupAmount, `${job.markup}× on cost — a ${job.marginPct.toFixed(0)}% margin`],
  ];
  if (job.addons > 0) rows.push(["Add-ons", job.addons, "gates, permit, travel — priced separately"]);
  if (job.slope > 0) rows.push(["Slope / grade", job.slope, "surcharge"]);
  return (
    <div className={"sample-job" + (compact ? " compact" : "")}>
      <div className="row between" style={{ gap: 10, flexWrap: "wrap" }}>
        <span className="kicker" style={{ margin: 0 }}>{title || "Sample job"}</span>
        {onInputs
          ? <span className="sample-inputs">
              <label>ft<input className="tnum" type="number" min="1" value={job.inputs.linearFeet}
                onChange={(e: any) => onInputs({ ...job.inputs, linearFeet: Math.max(1, +e.target.value || 0) })} /></label>
              <label>gates<input className="tnum" type="number" min="0" value={job.inputs.gates}
                onChange={(e: any) => onInputs({ ...job.inputs, gates: Math.max(0, +e.target.value || 0) })} /></label>
            </span>
          : <span className="faint" style={{ fontSize: 11 }}>{job.assumption}</span>}
      </div>
      <div className="sample-rows">
        {rows.map(([k, v, sub]: any) => (
          <div key={k} className="sample-row"><span>{k}<span className="faint"> — {sub}</span></span><span className="tnum">{money(v)}</span></div>
        ))}
        <div className="sample-row sub"><span>Subtotal</span><span className="tnum">{money(job.subtotal)}</span></div>
        {job.taxRate > 0 && <div className="sample-row"><span>{job.taxLabel} · {(job.taxRate * 100).toFixed(2).replace(/\.?0+$/, "")}%</span><span className="tnum">{money(job.tax)}</span></div>}
        <div className="sample-row total"><span>Customer total</span><span className="tnum">{money(job.customerTotal)}</span></div>
      </div>
      <div className="sample-foot">
        <b className="tnum">{money(job.perFoot)}/ft</b> installed
        <span className="faint">· {job.assumption}</span>
      </div>
    </div>
  );
}
